import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';

function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-cream-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center text-sage-600 hover:text-sage-700 mb-8 transition-colors">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-light text-sage-800 mb-6 leading-tight">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed mb-4">
            Last Updated: January 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="px-4 sm:px-6 lg:px-8 pb-20 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            
            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">Who We Are</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              HopefulPaths is a therapy and coaching practice dedicated to helping women break free from people-pleasing patterns and build authentic connections. Our website provides information about our services and allows visitors to schedule consultations.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">What Personal Data We Collect and Why</h2>
            
            <h3 className="text-2xl font-serif font-light text-sage-800 mt-8 mb-4">Contact Forms</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              When you fill out our contact or booking forms, we collect your name, email address, phone number (if provided), and any message you choose to share. This information is used solely to respond to your inquiry and schedule consultations. We do not share this information with third parties unless required by law.
            </p>

            <h3 className="text-2xl font-serif font-light text-sage-800 mt-8 mb-4">Comments</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              If visitors leave comments on the site, we collect the data shown in the comments form, and also the visitor's IP address and browser user agent string to help spam detection.
            </p>
            
            <p className="text-gray-600 leading-relaxed mb-6">
              An anonymized string created from your email address (also called a hash) may be provided to the Gravatar service to see if you are using it. The Gravatar service privacy policy is available here: <a href="https://automattic.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-sage-600 hover:text-sage-700 underline">https://automattic.com/privacy/</a>. After approval of your comment, your profile picture is visible to the public in the context of your comment.
            </p>

            <h3 className="text-2xl font-serif font-light text-sage-800 mt-8 mb-4">Media</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              If you upload images to the website, you should avoid uploading images with embedded location data (EXIF GPS) included. Visitors to the website can download and extract any location data from images on the website.
            </p>

            <h3 className="text-2xl font-serif font-light text-sage-800 mt-8 mb-4">Cookies</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              If you leave a comment on our site you may opt-in to saving your name, email address and website in cookies. These are for your convenience so that you do not have to fill in your details again when you leave another comment. These cookies will last for one year.
            </p>
            
            <p className="text-gray-600 leading-relaxed mb-6">
              If you visit our login page, we will set a temporary cookie to determine if your browser accepts cookies. This cookie contains no personal data and is discarded when you close your browser.
            </p>
            
            <p className="text-gray-600 leading-relaxed mb-6">
              When you log in, we will also set up several cookies to save your login information and your screen display choices. Login cookies last for two days, and screen options cookies last for a year. If you select "Remember Me", your login will persist for two weeks. If you log out of your account, the login cookies will be removed.
            </p>
            
            <p className="text-gray-600 leading-relaxed mb-6">
              If you edit or publish an article, an additional cookie will be saved in your browser. This cookie includes no personal data and simply indicates the post ID of the article you just edited. It expires after 1 day.
            </p>

            <h3 className="text-2xl font-serif font-light text-sage-800 mt-8 mb-4">Embedded Content from Other Websites</h3>
            <p className="text-gray-600 leading-relaxed mb-6">
              Articles on this site may include embedded content (e.g. videos, images, articles, etc.). Embedded content from other websites behaves in the exact same way as if the visitor has visited the other website.
            </p>
            
            <p className="text-gray-600 leading-relaxed mb-6">
              These websites may collect data about you, use cookies, embed additional third-party tracking, and monitor your interaction with that embedded content, including tracking your interaction with the embedded content if you have an account and are logged in to that website.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">Who We Share Your Data With</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              We do not sell, trade, or otherwise transfer your personal information to outside parties. If you request a password reset, your IP address will be included in the reset email.
            </p>
            
            <p className="text-gray-600 leading-relaxed mb-6">
              We may use trusted third-party services to help us operate our website or conduct our business, so long as those parties agree to keep this information confidential. Examples include:
            </p>
            <ul className="list-disc pl-8 space-y-2 text-gray-600 mb-6">
              <li>Email service providers (for sending appointment confirmations and newsletters, if you opt in)</li>
              <li>Calendar and scheduling tools (for managing consultations)</li>
              <li>Website hosting and analytics services</li>
            </ul>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">How Long We Retain Your Data</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              If you leave a comment, the comment and its metadata are retained indefinitely. This is so we can recognize and approve any follow-up comments automatically instead of holding them in a moderation queue.
            </p>
            
            <p className="text-gray-600 leading-relaxed mb-6">
              For users that register on our website (if any), we also store the personal information they provide in their user profile. All users can see, edit, or delete their personal information at any time (except they cannot change their username). Website administrators can also see and edit that information.
            </p>
            
            <p className="text-gray-600 leading-relaxed mb-6">
              Contact form submissions and inquiry data are retained for business purposes to maintain communication history and comply with professional record-keeping requirements.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">What Rights You Have Over Your Data</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              If you have an account on this site, or have left comments, you can request to receive an exported file of the personal data we hold about you, including any data you have provided to us. You can also request that we erase any personal data we hold about you. This does not include any data we are obliged to keep for administrative, legal, or security purposes.
            </p>
            
            <p className="text-gray-600 leading-relaxed mb-6">
              To make such a request, please contact us through our <Link to="/#booking" className="text-sage-600 hover:text-sage-700 underline">contact form</Link>.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">Where Your Data Is Sent</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Visitor comments may be checked through an automated spam detection service.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">HIPAA Compliance Notice</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Please note that this website and its contact forms are not HIPAA-compliant communication channels. Do not share Protected Health Information (PHI) or sensitive clinical information through website forms, comments, or email.
            </p>
            
            <p className="text-gray-600 leading-relaxed mb-6">
              If you become a client, we will provide you with secure, HIPAA-compliant communication channels for any clinical discussions or sharing of personal health information. The privacy practices for therapeutic relationships are covered in separate clinical agreements and informed consent documents.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">Your Consent</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              By using our website, you consent to our privacy policy.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">Changes to Our Privacy Policy</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last Updated" date at the top of this page.
            </p>
            
            <p className="text-gray-600 leading-relaxed mb-6">
              We encourage you to review this privacy policy periodically for any changes. Changes to this privacy policy are effective when they are posted on this page.
            </p>

            <h2 className="text-3xl font-serif font-light text-sage-800 mt-12 mb-6">Contact Us</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              If you have any questions about this Privacy Policy, please contact us through our <Link to="/#booking" className="text-sage-600 hover:text-sage-700 underline">contact form</Link>.
            </p>

            <div className="bg-sage-50 p-6 rounded-xl mt-12">
              <p className="text-sm text-gray-600">
                <strong>Note:</strong> This privacy policy covers our website only. It does not extend to any websites that can be accessed from this website including, but not limited to, any links we may provide to social media websites. If you follow a link to any of these websites, please note that these websites have their own privacy policies and that we do not accept any responsibility or liability for these policies.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default PrivacyPolicy;
